import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { AcquisitionCheckbox } from '@/components/acquisitions/acquisition-checkbox'
import { MarkdownContent } from '@/components/markdown/markdown-content'
import { formatUsd } from '@/features/projects/rollup-bom'
import type { StepReadiness } from '@/features/projects/step-readiness'
import type {
  CatalogMaterial,
  CatalogTool,
  InventoryItem,
  MaterialAcquisition,
  ProjectCatalog,
  StepNode,
  ToolAcquisition,
} from '@/api/types'

interface DoingStepRowProps {
  step: StepNode
  depth: number
  catalog: ProjectCatalog
  completedStepIds: Set<string>
  onToggleProgress: (stepId: string, completed: boolean) => void
  pendingStepIds: Set<string>
  /**
   * Phase 13: per-step readiness — blocked Action rows render muted with a "Blocked by …" badge,
   * Sections derive completion from descendants (purely visual), and the map is computed once at
   * the outline level. Optional so legacy preview surfaces still render.
   */
  readinessMap?: Map<string, StepReadiness>
  /** Phase 12: optional. When omitted (e.g., legacy preview surface), checkboxes don't render. */
  acquisitions?: {
    materials: Map<string, MaterialAcquisition>
    tools: Map<string, ToolAcquisition>
    inventory: InventoryItem[]
    onToggleMaterial: (snapshotAttachmentId: string, currentlyAcquired: boolean) => void
    onToggleTool: (catalogToolId: string, currentlyAcquired: boolean) => void
    pendingMaterialIds: Set<string>
    pendingToolIds: Set<string>
  }
}

const normalize = (v: string): string => v.trim().toLowerCase()

const findMaterial = (catalog: ProjectCatalog, id: string): CatalogMaterial | undefined =>
  catalog.materials.find((m) => m.id === id)

const findTool = (catalog: ProjectCatalog, id: string): CatalogTool | undefined =>
  catalog.tools.find((t) => t.id === id)

const formatQty = (qty: number): string => {
  if (Number.isInteger(qty)) return String(qty)
  return qty.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * One row of the doing-view outline. Mobile-first: a vertically stacked card with a tappable
 * checkbox region (≥44px square) on Action leaves, an inline kind badge, and material/tool chips
 * underneath. Only leaf Actions show the checkbox — Sections and Project references are
 * structural and never have progress state.
 *
 * <p>Indentation is left padding scaled by {@code depth}; we cap the visible step at depth 6 to
 * keep narrow viewports usable. Deep nesting collapses naturally because children render via the
 * recursive {@code DoingOutline} (defined alongside).
 */
export const DoingStepRow = ({
  step,
  depth,
  catalog,
  completedStepIds,
  onToggleProgress,
  pendingStepIds,
  readinessMap,
  acquisitions,
}: DoingStepRowProps) => {
  const [expanded, setExpanded] = useState(true)
  const isLeafAction = step.kind === 'ACTION' && step.children.length === 0
  const completed = completedStepIds.has(step.id)
  const pending = pendingStepIds.has(step.id)
  const indent = Math.min(depth, 6)
  const readiness = readinessMap?.get(step.id)
  const blockedView = isLeafAction && !completed && readiness?.blocked === true

  return (
    <li className="list-none" id={`step-${step.id}`}>
      <div className="flex items-start gap-3 py-2 pr-2" style={{ paddingLeft: `${indent * 12}px` }}>
        {isLeafAction ? (
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center"
            aria-label={completed ? `Mark ${step.title} incomplete` : `Mark ${step.title} complete`}
            onClick={() => onToggleProgress(step.id, !completed)}
            disabled={pending}
          >
            <Checkbox checked={completed} className="h-5 w-5" />
          </button>
        ) : step.children.length > 0 ? (
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
        ) : (
          <span className="h-11 w-11 shrink-0" aria-hidden />
        )}

        <div className="min-w-0 flex-1 space-y-1 pt-1.5">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={`text-base ${
                completed
                  ? 'text-muted-foreground line-through'
                  : blockedView
                    ? 'text-muted-foreground'
                    : 'font-medium'
              }`}
            >
              {step.title}
            </span>
            {step.kind === 'SECTION' && (
              <Badge variant="outline" className="text-xs">
                Section
              </Badge>
            )}
            {step.kind === 'PROJECT_REFERENCE' && (
              <Badge variant="outline" className="text-xs">
                from {step.referencedProjectTitle ?? 'project'}
              </Badge>
            )}
            {step.isMilestone && (
              <Badge variant="secondary" className="text-xs">
                Milestone
              </Badge>
            )}
          </div>
          {blockedView && readiness && readiness.blockedBy.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Blocked by:{' '}
              {readiness.blockedBy.map((dep, i) => (
                <span key={dep.id}>
                  {i > 0 && ', '}
                  <a
                    href={`#step-${dep.id}`}
                    className="underline-offset-2 hover:underline"
                    onClick={(e) => {
                      const target = document.getElementById(`step-${dep.id}`)
                      if (target) {
                        e.preventDefault()
                        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        target.classList.add('ring-2', 'ring-primary')
                        window.setTimeout(
                          () => target.classList.remove('ring-2', 'ring-primary'),
                          1500
                        )
                      }
                    }}
                  >
                    {dep.title}
                  </a>
                </span>
              ))}
            </p>
          )}
          {step.description && (
            <MarkdownContent value={step.description} className="text-sm text-muted-foreground" />
          )}
          {(step.materials.length > 0 || step.tools.length > 0) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {step.materials.map((sm) => {
                const m = findMaterial(catalog, sm.catalogMaterialId)
                if (!m) return null
                if (!acquisitions) {
                  return (
                    <span
                      key={sm.id}
                      className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                    >
                      {formatQty(sm.quantity)} {m.unit} {m.name}
                    </span>
                  )
                }
                const acq = acquisitions.materials.get(sm.id)
                const checked = Boolean(acq)
                const lockedPrice =
                  acq?.lockedUnitPriceCents != null ? acq.lockedUnitPriceCents : null
                const annotation =
                  lockedPrice != null ? `${formatUsd(lockedPrice)}/${m.unit}` : null
                return (
                  <AcquisitionCheckbox
                    key={sm.id}
                    checked={checked}
                    onChange={() => acquisitions.onToggleMaterial(sm.id, checked)}
                    label={`${formatQty(sm.quantity)} ${m.unit} ${m.name}`}
                    lockedPriceAnnotation={annotation}
                    pending={acquisitions.pendingMaterialIds.has(sm.id)}
                  />
                )
              })}
              {step.tools.map((st) => {
                const t = findTool(catalog, st.catalogToolId)
                if (!t) return null
                if (!acquisitions) {
                  return (
                    <span
                      key={st.id}
                      className="inline-flex items-center gap-1 rounded border border-dashed px-1.5 py-0.5 text-xs text-muted-foreground"
                    >
                      {t.name}
                    </span>
                  )
                }
                const acq = acquisitions.tools.get(st.catalogToolId)
                const checked = Boolean(acq)
                const inInventory = acquisitions.inventory.some(
                  (i) => normalize(i.name) === normalize(t.name)
                )
                return (
                  <AcquisitionCheckbox
                    key={st.id}
                    checked={checked || inInventory}
                    onChange={() => acquisitions.onToggleTool(st.catalogToolId, checked)}
                    label={t.name}
                    inventoryBadge={inInventory && !checked}
                    pending={acquisitions.pendingToolIds.has(st.catalogToolId)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {expanded && step.children.length > 0 && (
        <ul className="space-y-0.5">
          {step.children.map((child) => (
            <DoingStepRow
              key={child.id}
              step={child}
              depth={depth + 1}
              catalog={catalog}
              completedStepIds={completedStepIds}
              onToggleProgress={onToggleProgress}
              pendingStepIds={pendingStepIds}
              readinessMap={readinessMap}
              acquisitions={acquisitions}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
